import React, { useState, useRef } from 'react';
import { Upload, X, FileText, Video, Music, Image, File, Loader2, CheckCircle } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const FILE_TYPE_CONFIG = {
  image: { icon: Image, color: 'text-blue-500', accept: '.jpg,.jpeg,.png,.gif,.webp' },
  mp4: { icon: Video, color: 'text-purple-500', accept: '.mp4,.webm,.mov' },
  mp3: { icon: Music, color: 'text-green-500', accept: '.mp3,.wav,.ogg,.aac' },
  pdf: { icon: FileText, color: 'text-red-500', accept: '.pdf' },
  doc: { icon: File, color: 'text-orange-500', accept: '.doc,.docx' },
  file: { icon: File, color: 'text-gray-500', accept: '*' }
};

const ALL_ACCEPTS = '.jpg,.jpeg,.png,.gif,.webp,.mp4,.webm,.mov,.mp3,.wav,.ogg,.aac,.pdf,.doc,.docx';

const FileUploader = ({ 
  contentType = 'product', // product, course, pack
  onFileUploaded,
  multiple = true,
  existingFiles = [],
  onFileRemoved
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  // Ensure existingFiles is always an array
  const safeExistingFiles = Array.isArray(existingFiles) ? existingFiles : [];

  const getFileIcon = (fileType) => {
    const config = FILE_TYPE_CONFIG[fileType] || FILE_TYPE_CONFIG.file;
    const Icon = config.icon;
    return <Icon className={`w-5 h-5 ${config.color}`} />;
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);

    for (const file of files) {
      const fileId = `${file.name}-${Date.now()}`;
      setUploadProgress(prev => ({ ...prev, [fileId]: { name: file.name, progress: 0 } }));

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post(`/upload/content/${contentType}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({
              ...prev,
              [fileId]: { ...prev[fileId], progress }
            }));
          }
        });

        if (response.data.success) {
          onFileUploaded({
            name: response.data.original_name || file.name,
            url: response.data.url,
            file_type: response.data.file_type,
            size_mb: response.data.size_mb
          });
          toast.success(`${file.name} uploaded successfully!`);
        }
      } catch (error) {
        console.error('Upload failed:', error);
        toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || error.message}`);
      } finally {
        setUploadProgress(prev => {
          const updated = { ...prev };
          delete updated[fileId];
          return updated;
        });
      }
    }

    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (sizeMb) => {
    if (sizeMb < 1) return `${Math.round(sizeMb * 1024)} KB`;
    return `${sizeMb.toFixed(2)} MB`;
  };

  return (
    <div className="space-y-4">
      {/* Upload Button */}
      <div 
        className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ALL_ACCEPTS}
          multiple={multiple}
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-upload-input"
        />
        
        <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm font-medium mb-1">
          Click to upload or drag & drop
        </p>
        <p className="text-xs text-muted-foreground">
          Supported: JPEG, GIF, PNG, MP4, MP3, PDF (No size limit)
        </p>
      </div>

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="space-y-2">
          {Object.entries(uploadProgress).map(([id, { name, progress }]) => (
            <div key={id} className="bg-white/5 rounded-lg p-3">
              <div className="flex items-center gap-3 mb-2">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span className="text-sm truncate flex-1">{name}</span>
                <span className="text-xs text-muted-foreground">{progress}%</span>
              </div>
              <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Existing Files */}
      {safeExistingFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">Uploaded Files ({safeExistingFiles.length})</p>
          {safeExistingFiles.map((file, idx) => (
            <div 
              key={idx} 
              className="flex items-center gap-3 bg-white/5 rounded-lg p-3 group"
              data-testid={`uploaded-file-${idx}`}
            >
              {getFileIcon(file?.file_type)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{file?.name || file?.url || 'Unknown file'}</p>
                <p className="text-xs text-muted-foreground">
                  {file?.file_type?.toUpperCase() || 'FILE'} {file?.size_mb && `• ${formatFileSize(file.size_mb)}`}
                </p>
              </div>
              <CheckCircle className="w-4 h-4 text-green-500" />
              {onFileRemoved && (
                <button
                  type="button"
                  onClick={() => onFileRemoved(idx)}
                  className="p-1 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4 text-red-500" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUploader;
